import type {
  VhAdminAudience,
  VhAdminAudiencePayload,
  VhAdminCurrentUser,
  VhAdminFeature,
  VhAdminFeatureType,
  VhAdminGetAllPeopleParams,
  VhAdminGetVesselsParams,
  VhAdminGrantSubscriptionPayload,
  VhAdminServicePlan,
  VhAdminGetServiceProvidersParams,
  VhAdminServiceProvider,
  VhAdminServiceProvidersPage,
  VhAdminSystemRole,
  VhAdminUsersPage,
  VhAdminUser,
  VhAdminVesselsPage,
  VhAdminWhitelistAuditPage,
  VhAdminWhitelistEntry,
  VhAdminWhitelistPage,
  VhAdminWhitelistParams,
  VhAdminWhitelistPayload,
  VhAdminWhitelistSettings,
} from '@/admin/admin.types';
import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';
import { isNotFound } from '@/common/error';

/** GET /user/me on AUTH — the only source of the system role (admin / super_admin). */
export const vhAdmGetCurrentUser = async (): Promise<VhAdminCurrentUser> => {
  const response = await getRequest<VhAdminCurrentUser>(API.AUTH, '/user/me');
  return response.data;
};

/** GET /user/:id on AUTH: the system role (user / admin / super-admin), which PEOPLE doesn't carry. */
export const vhAdmGetAuthUser = async (id: string): Promise<VhAdminCurrentUser> => {
  const response = await getRequest<VhAdminCurrentUser>(API.AUTH, `/user/${id}`);
  return response.data;
};

/**
 * The system role of several people at once (person id = auth user id). Ids without an account
 * are left out; when none of them has one, auth answers 404, read here as none.
 */
export const vhAdmGetAuthUsers = async (ids: string[]): Promise<VhAdminCurrentUser[]> => {
  try {
    const response = await postRequest<VhAdminCurrentUser[], { user_ids: string[] }>(
      API.AUTH,
      '/user/find',
      { user_ids: ids },
    );
    return response.data;
  } catch (error) {
    if (isNotFound(error)) return [];
    throw error;
  }
};

/**
 * Super admins only, and the one way a system role changes. A super admin's own role is refused
 * (409), so nobody can demote one here.
 */
export const vhAdmAssignSystemRole = async (id: string, role: VhAdminSystemRole): Promise<void> => {
  await postRequest(API.AUTH, `/user/assign-role/${id}`, { role });
};

export const vhAdmGetAllPeople = async (
  params: VhAdminGetAllPeopleParams,
): Promise<VhAdminUsersPage> => {
  const response = await getRequest<VhAdminUsersPage>(API.PEOPLE, '/all', false, params);
  return response.data;
};

/** People by id in the same shape as /all, so one off the current page can still be opened. */
export const vhAdmGetPeopleByIds = async (ids: string[]): Promise<VhAdminUser[]> => {
  const response = await getRequest<VhAdminUser[]>(API.PEOPLE, '/ids', false, { ids });
  return response.data;
};

export const vhAdmSetPersonAudience = async (
  personId: string,
  audienceId: string,
): Promise<void> => {
  await patchRequest(API.PEOPLE, `/${personId}/beta-group`, { group_id: audienceId });
};

export const vhAdmGetBetaFeatures = async (): Promise<VhAdminFeature[]> => {
  const response = await getRequest<VhAdminFeature[]>(API.PEOPLE, '/beta-features');
  return response.data;
};

export const vhAdmMarkBetaFeature = async (
  feature: string,
  type: VhAdminFeatureType,
): Promise<void> => {
  await postRequest(API.PEOPLE, '/beta-features', { feature, type });
};

/** Unmarking a frontend feature also drops it from every audience holding it. */
export const vhAdmUnmarkBetaFeature = async (feature: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/beta-features/${encodeURIComponent(feature)}`, undefined);
};

export const vhAdmGetAudiences = async (): Promise<VhAdminAudience[]> => {
  const response = await getRequest<VhAdminAudience[]>(API.PEOPLE, '/beta-features/groups');
  return response.data;
};

export const vhAdmCreateAudience = async (
  payload: VhAdminAudiencePayload,
): Promise<VhAdminAudience> => {
  const response = await postRequest<VhAdminAudience, VhAdminAudiencePayload>(
    API.PEOPLE,
    '/beta-features/groups',
    payload,
  );
  return response.data;
};

/** Replaces name *and* features — partial updates aren't a thing on this endpoint. */
export const vhAdmUpdateAudience = async (
  id: string,
  payload: VhAdminAudiencePayload,
): Promise<void> => {
  await putRequest(API.PEOPLE, `/beta-features/groups/${id}`, payload);
};

export const vhAdmDeleteAudience = async (id: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/beta-features/groups/${id}`, undefined);
};

export const vhAdmGetVessels = async (
  params: VhAdminGetVesselsParams,
): Promise<VhAdminVesselsPage> => {
  const response = await getRequest<VhAdminVesselsPage>(API.VESSEL, '', false, params);
  return response.data;
};

/**
 * The live plan of each vessel, up to 100 at once — seat counts and whether an admin granted it,
 * which the vessel list doesn't carry. A vessel on no live plan is left out: it is on Harbor.
 */
export const vhAdmGetServicePlans = async (serviceIds: string[]): Promise<VhAdminServicePlan[]> => {
  const response = await getRequest<VhAdminServicePlan[]>(
    API.PEOPLE,
    '/subscriptions/services',
    false,
    { ids: serviceIds },
  );
  return response.data;
};

/** Name, logo and type of each provider, up to 100 at once; one that no longer exists is left out. */
export const vhAdmGetServiceProviders = async (
  ids: string[],
): Promise<VhAdminServiceProvider[]> => {
  // This client has no paramsSerializer, so an array would go out as ids[]=… and never bind.
  const params = new URLSearchParams(ids.map((id) => ['ids', id]));
  const response = await getRequest<VhAdminServiceProvider[]>(
    API.SERVICE_PROVIDERS,
    'service-provider/admin/info',
    false,
    params,
  );
  return response.data;
};

/** Super admins only: deletes a vessel whoever owns it, with everything its owner's delete removes. */
export const vhAdmDeleteVessel = async (id: string): Promise<void> => {
  await deleteRequest(API.VESSEL, `/${id}`, undefined);
};

/** Every service provider, vessels' private vendors included, newest first. */
export const vhAdmGetServiceProvidersPage = async (
  params: VhAdminGetServiceProvidersParams,
): Promise<VhAdminServiceProvidersPage> => {
  const response = await getRequest<VhAdminServiceProvidersPage>(
    API.SERVICE_PROVIDERS,
    'service-provider/admin',
    false,
    params,
  );
  return response.data;
};

/** Super admins only: deletes a provider whoever created it, notifying its members. */
export const vhAdmDeleteServiceProvider = async (id: string): Promise<void> => {
  await deleteRequest(API.SERVICE_PROVIDERS, `service-provider/admin/${id}`, undefined);
};

/** Replaces the vessel's plan with one that never expires, cancelling any live Stripe one first. */
export const vhAdmGrantSubscription = async (
  payload: VhAdminGrantSubscriptionPayload,
): Promise<void> => {
  await postRequest(API.PEOPLE, '/subscriptions/grant', payload);
};

/** Takes a granted lifetime plan back, leaving the vessel and everyone on it on Harbor. */
export const vhAdmRevokeSubscription = async (serviceId: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/subscriptions/grant/${serviceId}`, undefined);
};

export const vhAdmGetWhitelist = async (
  params: VhAdminWhitelistParams,
): Promise<VhAdminWhitelistPage> => {
  const response = await getRequest<VhAdminWhitelistPage>(API.AUTH, '/whitelist', false, params);
  return response.data;
};

export const vhAdmGetWhitelistSettings = async (): Promise<VhAdminWhitelistSettings> => {
  const response = await getRequest<VhAdminWhitelistSettings>(API.AUTH, '/whitelist/settings');
  return response.data;
};

export const vhAdmGetWhitelistAudit = async (params: {
  page: number;
  limit: number;
}): Promise<VhAdminWhitelistAuditPage> => {
  const response = await getRequest<VhAdminWhitelistAuditPage>(
    API.AUTH,
    '/whitelist/audit',
    false,
    params,
  );
  return response.data;
};

export const vhAdmCreateWhitelistEntry = async (
  payload: VhAdminWhitelistPayload,
): Promise<VhAdminWhitelistEntry> => {
  const response = await postRequest<VhAdminWhitelistEntry, VhAdminWhitelistPayload>(
    API.AUTH,
    '/whitelist',
    payload,
  );
  return response.data;
};

export const vhAdmSetWhitelistEnabled = async (
  enabled: boolean,
): Promise<VhAdminWhitelistSettings> => {
  const response = await putRequest<VhAdminWhitelistSettings, { enabled: boolean }>(
    API.AUTH,
    '/whitelist/settings',
    { enabled },
  );
  return response.data;
};

/** Replaces contact, name *and* description. */
export const vhAdmUpdateWhitelistEntry = async (
  id: string,
  payload: VhAdminWhitelistPayload,
): Promise<VhAdminWhitelistEntry> => {
  const response = await putRequest<VhAdminWhitelistEntry, VhAdminWhitelistPayload>(
    API.AUTH,
    `/whitelist/${id}`,
    payload,
  );
  return response.data;
};

/**
 * With `account`, every entry linked to the same account goes too. While gated access is on, the
 * account is signed out once none of its contacts is left on the list.
 */
export const vhAdmDeleteWhitelistEntry = async (id: string, account: boolean): Promise<void> => {
  await deleteRequest(API.AUTH, `/whitelist/${id}?account=${account}`, undefined);
};
